/* SUMMARY
  * Imports
  * PreferencesInitializer: réconcilie le miroir local avec le serveur au démarrage
*/

/* Imports */
import { useEffect, useRef } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { fetchUserPreferences } from "../../services/preferences/userPreferences";
import { writeTablePageSize } from "../../services/preferences/tablePageSize";
/***/

/**
* PreferencesInitializer: aligne le miroir local sur ce que le serveur détient, une fois par session.
*
* Le miroir `localStorage` est ce que les tableaux lisent au premier rendu ; le serveur est la
* source de vérité. Ce composant est le point où les deux se rejoignent, à l'image de
* `LanguageInitializer` — mais en appelant `users/me/preferences` et non `admin/users/{id}`, qui
* renvoie 403 au planificateur et rendrait la fonctionnalité morte pour l'utilisateur visé.
*
* Ne bloque jamais l'affichage : faire attendre le premier rendu de quinze écrans pour éviter un
* seul re-rendu à la toute première connexion serait un mauvais échange. Un échec — back pas encore
* déployé, réseau coupé — laisse simplement le miroir en place, donc la dernière taille connue.
*
* L'écriture est inconditionnelle, y compris quand le serveur répond `null` : c'est ce qui permet à
* un retour au défaut fait sur un autre poste de redescendre ici plutôt que de rester bloqué sur
* une ancienne valeur locale.
*/
export default function PreferencesInitializer({ children }: { children: React.ReactNode }) {
  const { keycloak } = useKeycloak();
  // Une ref plutôt qu'un state : ce drapeau ne doit rien redessiner, seulement empêcher un second
  // appel quand Keycloak rafraîchit son jeton.
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !keycloak.authenticated) return;
    loaded.current = true;

    fetchUserPreferences()
      .then((preferences) => writeTablePageSize(preferences.tablePageSize))
      .catch((error) => console.error("Erreur lors du chargement des préférences:", error));
  }, [keycloak.authenticated]);

  return <>{children}</>;
}
/***/
